// src/components/Calendar.js
import React, { useState, useEffect, useContext } from 'react';
import { Container, Fab } from '@mui/material';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem } from '@mui/material';
import { Add } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { AuthContext } from '../utils/AuthContext';
import { getCircuits, addEvent, updateEvent, deleteEvent, joinEvent, getEvents, addPost } from '../utils/firestoreUtils';
import { getDoc, doc } from 'firebase/firestore';
import { db, storage } from '../utils/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Compressor from 'compressorjs';
import 'flag-icons/css/flag-icons.min.css';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function fmt(d) {
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Renders a CSS flag icon from a 2-letter ISO country code
function FlagIcon({ code, size = 16, style = {} }) {
  if (!code) return null;
  const lower = code.trim().toLowerCase();
  return (
    <span
      className={`fi fi-${lower}`}
      style={{ width: size, height: size * 0.75, borderRadius: 2, display: 'inline-block', flexShrink: 0, ...style }}
    />
  );
}

// ── Create/Edit Event Dialog ──────────────────────────────────
function EventFormDialog({ open, onClose, circuits, userId, userName, editEvent, onSaved }) {
  const [name,        setName]        = useState('');
  const [date,        setDate]        = useState('');
  const [circuitId,   setCircuitId]   = useState('');
  const [signupUrl,   setSignupUrl]   = useState('');
  const [description, setDescription] = useState('');
  const [images,      setImages]      = useState([]);
  const [shareToComm, setShareToComm] = useState(false);
  const [errors,      setErrors]      = useState({});
  const [saving,      setSaving]      = useState(false);

  useEffect(() => {
    if (editEvent) {
      setName(editEvent.name || '');
      setDate(editEvent.date || '');
      setCircuitId(editEvent.circuitId || '');
      setSignupUrl(editEvent.signupUrl || '');
      setDescription(editEvent.description || '');
      setImages(editEvent.images || []);
    } else {
      setName(''); setDate(''); setCircuitId(''); setSignupUrl('');
      setDescription(''); setImages([]);
    }
    setErrors({}); setShareToComm(false);
  }, [editEvent, open]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file || images.length >= 8) return;
    new Compressor(file, {
      quality: 0.8, maxWidth: 1600, maxHeight: 1200,
      success: async (compressed) => {
        const r = ref(storage, `events/${Date.now()}_${file.name}`);
        await uploadBytes(r, compressed);
        const url = await getDownloadURL(r);
        setImages(prev => [...prev, url]);
      },
    });
  };

  const validate = () => {
    const e = {};
    if (!name.trim()) e.name = true;
    if (!date) e.date = true;
    if (!circuitId) e.circuitId = true;
    if (!signupUrl.trim() || !/^https?:\/\//.test(signupUrl)) e.signupUrl = true;
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    const circuit = circuits.find(c => c.id === circuitId);
    const payload = {
      name, date, circuitId,
      circuitName: circuit?.name || '',
      country: circuit?.country || '',
      signupUrl, description, images,
      createdBy: userId, createdByName: userName,
      attendees: editEvent?.attendees || [],
      updatedAt: new Date().toISOString(),
    };
    if (editEvent) {
      await updateEvent(editEvent.id, payload);
    } else {
      payload.createdAt = new Date().toISOString();
      const newId = await addEvent(payload);
      if (shareToComm) {
        const [y, m, d] = date.split('-').map(Number);
        const dateStr = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        const countryStr = circuit?.country ? ` · ${circuit.country.toUpperCase()}` : '';
        // Post content with event link — /calendar?event=ID so community can jump to it
        const postContent = `🏁 New Event: ${name}\n📅 ${dateStr}\n🏎 ${circuit?.name || ''}${countryStr}\n${description ? '\n' + description + '\n' : ''}\n🔗 View event & sign up: /calendar?event=${newId}`;
        await addPost({
          userId, userName,
          type: 'event',
          content: postContent,
          image: images[0] || null,
          eventId: newId,
          timestamp: new Date(),
          likes: [], comments: [],
        });
      }
    }
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-xl)' } }}>
      <DialogTitle sx={{ p: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', letterSpacing: '0.04em', lineHeight: 1 }}>
              {editEvent ? 'Edit Event' : 'Create Event'}
            </div>
            <div style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)', marginTop: 4 }}>Track day · Time attack · Club event</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', display: 'flex', padding: 4, borderRadius: '50%' }}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
      </DialogTitle>

      <DialogContent sx={{ p: '20px 24px' }}>
        <div className="field-section"><div className="field-section-line" /><span className="field-section-label">Event Details</span><div className="field-section-line" /></div>

        <TextField label="Event Name" value={name}
          onChange={e => { setName(e.target.value); setErrors(p => ({...p, name: false})); }}
          error={errors.name} helperText={errors.name ? 'Required' : ''}
          size="small" fullWidth sx={{ mb: 1.5 }} />

        <div className="field-row" style={{ marginBottom: 14 }}>
          <TextField type="date" label="Date" value={date}
            onChange={e => { setDate(e.target.value); setErrors(p => ({...p, date: false})); }}
            error={errors.date} size="small" fullWidth InputLabelProps={{ shrink: true }} />
          <TextField select label="Circuit" value={circuitId}
            onChange={e => { setCircuitId(e.target.value); setErrors(p => ({...p, circuitId: false})); }}
            error={errors.circuitId} size="small" fullWidth>
            {circuits.map(c => (
              <MenuItem key={c.id} value={c.id}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {c.country && <FlagIcon code={c.country} size={14} />}
                  {c.name}
                </span>
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div className="field-section"><div className="field-section-line" /><span className="field-section-label">Details</span><div className="field-section-line" /></div>

        <TextField label="Sign-up / Registration URL" value={signupUrl}
          onChange={e => { setSignupUrl(e.target.value); setErrors(p => ({...p, signupUrl: false})); }}
          error={errors.signupUrl} helperText={errors.signupUrl ? 'Valid URL required (https://…)' : ''}
          size="small" fullWidth sx={{ mb: 1.5 }} placeholder="https://..." />
        <TextField label="Description (optional)" value={description}
          onChange={e => setDescription(e.target.value)}
          multiline rows={3} size="small" fullWidth sx={{ mb: 1 }} />

        <div className="field-section"><div className="field-section-line" /><span className="field-section-label">Photos</span><div className="field-section-line" /></div>
        <label className="file-upload-label" htmlFor="event-img-upload">
          ＋ Add Photo {images.length > 0 ? `(${images.length}/8)` : ''}
        </label>
        <input id="event-img-upload" type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
        {images.length > 0 && (
          <div className="image-strip">
            {images.map((img, i) => (
              <div key={i} className="image-thumb">
                <img src={img} alt="" style={{ borderColor: i === 0 ? 'var(--accent)' : 'transparent' }} />
                <button className="image-thumb-del" onClick={() => setImages(prev => prev.filter((_, idx) => idx !== i))}>✕</button>
              </div>
            ))}
          </div>
        )}

        {!editEvent && (
          <label className="community-toggle" style={{ marginTop: 12 }}>
            <input type="checkbox" checked={shareToComm} onChange={e => setShareToComm(e.target.checked)} />
            <span className="community-toggle-label">Share to Community</span>
          </label>
        )}

        <div className="modal-actions">
          <div />
          <Button onClick={handleSave} disabled={saving} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}>
            {saving ? 'Saving…' : editEvent ? 'Save Changes' : 'Create Event'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Event Detail Dialog ───────────────────────────────────────
function EventDetailDialog({ open, onClose, event, userId, isOwner, onEdit, onDelete, onJoinChange }) {
  const [lightbox,         setLightbox]         = useState(null);
  const [joining,          setJoining]          = useState(false);
  const [localAttendees,   setLocalAttendees]   = useState([]);
  const [attendeeNames,    setAttendeeNames]    = useState([]);
  const [loadingAttendees, setLoadingAttendees] = useState(false);

  useEffect(() => {
    setLocalAttendees(event?.attendees || []);
    setLightbox(null);
  }, [event]);

  useEffect(() => {
    if (!localAttendees.length) { setAttendeeNames([]); return; }
    setLoadingAttendees(true);
    Promise.all(
      localAttendees.map(uid =>
        getDoc(doc(db, 'users', uid))
          .then(snap => snap.exists() ? (snap.data().displayName || 'Unknown') : 'Unknown')
          .catch(() => 'Unknown')
      )
    ).then(names => { setAttendeeNames(names); setLoadingAttendees(false); });
  }, [localAttendees.length]);

  if (!event) return null;
  const isJoined = localAttendees.includes(userId);
  const imgs = event.images || [];

  const handleJoin = async () => {
    if (!userId) return;
    setJoining(true);
    const joined = await joinEvent(event.id, userId);
    setLocalAttendees(prev => joined ? [...prev, userId] : prev.filter(id => id !== userId));
    onJoinChange?.();
    setJoining(false);
  };

  // Country display name lookup (for proper names if code is stored)
  const countryNames = { jp: 'Japan', us: 'United States', ca: 'Canada', gb: 'United Kingdom', de: 'Germany', fr: 'France', it: 'Italy', es: 'Spain', au: 'Australia', nl: 'Netherlands', be: 'Belgium', pt: 'Portugal', at: 'Austria', ch: 'Switzerland', se: 'Sweden', no: 'Norway', dk: 'Denmark', fi: 'Finland', pl: 'Poland', cz: 'Czech Republic', hu: 'Hungary', ro: 'Romania', sk: 'Slovakia', hr: 'Croatia', si: 'Slovenia', rs: 'Serbia', bg: 'Bulgaria', gr: 'Greece', tr: 'Turkey', mx: 'Mexico', br: 'Brazil', ar: 'Argentina', za: 'South Africa', cn: 'China', kr: 'South Korea', tw: 'Taiwan', sg: 'Singapore', nz: 'New Zealand' };
  const countryCode = event.country?.trim().toLowerCase();
  const countryDisplay = countryNames[countryCode] || (event.country ? event.country.toUpperCase() : null);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-xl)' } }}>

        <DialogTitle sx={{ p: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', letterSpacing: '0.03em', lineHeight: 1.05, color: 'var(--text-primary)' }}>
                {event.name}
              </div>
              {/* Date only — no flag here */}
              <div style={{ fontSize: '0.82rem', color: 'var(--accent)', fontWeight: 600, marginTop: 6 }}>
                {fmt(event.date)}
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 4, borderRadius: '50%', flexShrink: 0 }}>
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </DialogTitle>

        <DialogContent sx={{ p: '20px 24px' }}>
          {imgs[0] && (
            <img src={imgs[0]} alt="" className="event-detail-img"
              onClick={() => setLightbox(imgs[0])} style={{ cursor: 'zoom-in' }} />
          )}

          {/* Meta table — clean, no duplicate country */}
          <div style={{ background: 'var(--surface-2)', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: 14, border: '1px solid var(--border)' }}>
            <div className="event-meta-row">
              <span className="event-meta-label">Circuit</span>
              <span style={{ fontWeight: 600 }}>{event.circuitName}</span>
            </div>
            {countryDisplay && (
              <div className="event-meta-row">
                <span className="event-meta-label">Country</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {countryDisplay}
                  {countryCode && <FlagIcon code={countryCode} size={18} />}
                </span>
              </div>
            )}
            <div className="event-meta-row">
              <span className="event-meta-label">Date</span>
              <span>{fmt(event.date)}</span>
            </div>
            <div className="event-meta-row" style={{ borderBottom: 'none' }}>
              <span className="event-meta-label">Attending</span>
              <span style={{ fontWeight: 600 }}>{localAttendees.length} {localAttendees.length === 1 ? 'person' : 'people'}</span>
            </div>
          </div>

          {/* Who's Going */}
          {localAttendees.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 8 }}>
                Who's Going
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {loadingAttendees ? (
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-tertiary)' }}>Loading…</span>
                ) : (
                  attendeeNames.map((aName, i) => (
                    <span key={i} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      background: localAttendees[i] === userId ? 'var(--accent-subtle)' : 'var(--surface-2)',
                      border: `1px solid ${localAttendees[i] === userId ? 'rgba(25,130,206,0.2)' : 'var(--border)'}`,
                      borderRadius: 'var(--radius-pill)',
                      padding: '4px 10px',
                      fontSize: '0.8rem',
                      fontWeight: localAttendees[i] === userId ? 600 : 400,
                      color: localAttendees[i] === userId ? 'var(--accent)' : 'var(--text-secondary)',
                    }}>
                      {localAttendees[i] === userId && '✓ '}{aName}
                    </span>
                  ))
                )}
              </div>
            </div>
          )}

          {event.description && (
            <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 14 }}>
              {event.description}
            </p>
          )}

          {imgs.length > 1 && (
            <div className="event-detail-images">
              {imgs.slice(1).map((img, i) => (
                <img key={i} src={img} alt="" className="event-detail-img-thumb" onClick={() => setLightbox(img)} />
              ))}
            </div>
          )}

          <a href={event.signupUrl} target="_blank" rel="noopener noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', textDecoration: 'none', fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8, transition: 'background var(--transition)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-subtle)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-2)'}>
            <span style={{ color: 'var(--accent)', fontSize: '1rem' }}>↗</span>
            Sign Up / Register
          </a>

          {userId && (
            <button className={`join-btn ${isJoined ? 'joined' : ''}`} onClick={handleJoin} disabled={joining}>
              {joining ? '…' : isJoined ? "✓ You're going — click to withdraw" : '+ Mark as Attending'}
            </button>
          )}

          {isOwner && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={onEdit}
                style={{ flex: 1, padding: '9px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', transition: 'background var(--transition)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-subtle)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-2)'}>
                Edit Event
              </button>
              <button onClick={onDelete}
                style={{ flex: 1, padding: '9px', background: 'rgba(255,59,48,0.07)', border: '1px solid rgba(255,59,48,0.2)', borderRadius: 'var(--radius-md)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.82rem', fontWeight: 600, color: 'var(--red)', transition: 'background var(--transition)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,59,48,0.14)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,59,48,0.07)'}>
                Delete Event
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <img className="lightbox-img" src={lightbox} alt="" onClick={e => e.stopPropagation()} />
          <button className="lightbox-close" onClick={() => setLightbox(null)}>✕</button>
        </div>
      )}
    </>
  );
}

// ── Main Calendar ─────────────────────────────────────────────
function Calendar() {
  const { user } = useContext(AuthContext);
  const today = new Date();
  const [viewYear,   setViewYear]   = useState(today.getFullYear());
  const [viewMonth,  setViewMonth]  = useState(today.getMonth());
  const [events,     setEvents]     = useState([]);
  const [circuits,   setCircuits]   = useState([]);
  const [openForm,   setOpenForm]   = useState(false);
  const [editEvt,    setEditEvt]    = useState(null);
  const [detailEvt,  setDetailEvt]  = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);

  // Support opening a specific event via ?event=ID in the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const eventId = params.get('event');
    if (eventId) {
      getEvents().then(evts => {
        const found = evts.find(e => e.id === eventId);
        if (found) setDetailEvt(found);
      });
    }
  }, []);

  const loadData = async () => {
    const [evts, circs] = await Promise.all([getEvents(), getCircuits()]);
    setEvents(evts);
    setCircuits(circs);
  };

  useEffect(() => { loadData(); }, []);

  const prevMonth = () => { if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); } else setViewMonth(m => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); } else setViewMonth(m => m + 1); };

  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrev  = new Date(viewYear, viewMonth, 0).getDate();
  const totalCells  = Math.ceil((firstDay + daysInMonth) / 7) * 7;

  const cells = Array.from({ length: totalCells }, (_, i) => {
    const n = i - firstDay + 1;
    if (n < 1)           return { day: daysInPrev + n,  month: viewMonth - 1, year: viewYear, current: false };
    if (n > daysInMonth) return { day: n - daysInMonth, month: viewMonth + 1, year: viewYear, current: false };
    return                      { day: n,               month: viewMonth,     year: viewYear, current: true  };
  });

  const eventsOnDay = (day, month, year) => {
    const iso = `${year}-${String(month + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return events.filter(e => e.date === iso);
  };

  const isToday = (day, month, year) =>
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const upcomingEvents = events
    .filter(e => e.date >= today.toISOString().slice(0, 10))
    .slice(0, 12);

  const handleDelete = async () => {
    await deleteEvent(detailEvt.id);
    setConfirmDel(false);
    setDetailEvt(null);
    loadData();
  };

  return (
    <Container maxWidth="xl" sx={{ pb: 10 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 className="page-title">Events Calendar</h1>
        <p className="page-subtitle">Upcoming track days and time attack events</p>
      </div>

      <div className="cal-wrap">
        {/* Grid */}
        <div>
          <div className="cal-nav">
            <button className="cal-nav-btn" onClick={prevMonth}>‹</button>
            <span className="cal-month-label">{MONTHS[viewMonth]} {viewYear}</span>
            <button className="cal-nav-btn" onClick={nextMonth}>›</button>
          </div>
          <div className="cal-grid">
            {DAYS_OF_WEEK.map(d => <div key={d} className="cal-dow">{d}</div>)}
            {cells.map((cell, i) => {
              const dayEvents = eventsOnDay(cell.day, cell.month, cell.year);
              const todayCell = isToday(cell.day, cell.month, cell.year);
              return (
                <div key={i} className={['cal-day', !cell.current ? 'other-month' : '', todayCell ? 'today' : '', dayEvents.length ? 'has-events' : ''].filter(Boolean).join(' ')}>
                  <div className="cal-day-num">{cell.day}</div>
                  {dayEvents.slice(0, 2).map(ev => (
                    <div key={ev.id} className="cal-event-dot" onClick={() => setDetailEvt(ev)}>
                      {ev.country && <FlagIcon code={ev.country} size={11} style={{ borderRadius: 1 }} />}
                      <span className="cal-event-dot-label">{ev.name}</span>
                    </div>
                  ))}
                  {dayEvents[0] && (
                    <div style={{ fontSize: '0.58rem', color: 'var(--text-tertiary)', paddingLeft: 3, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {dayEvents[0].circuitName}
                    </div>
                  )}
                  {dayEvents.length > 2 && (
                    <div style={{ fontSize: '0.58rem', color: 'var(--text-tertiary)', paddingLeft: 3 }}>+{dayEvents.length - 2} more</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar */}
        <div className="cal-sidebar">
          <div className="cal-sidebar-header">
            <span className="cal-sidebar-title">Upcoming Events</span>
            <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{upcomingEvents.length}</span>
          </div>
          {upcomingEvents.length === 0 ? (
            <div className="cal-sidebar-empty">No upcoming events yet</div>
          ) : (
            upcomingEvents.map(ev => (
              <div key={ev.id} className="event-card" onClick={() => setDetailEvt(ev)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  {ev.country && <FlagIcon code={ev.country} size={13} />}
                  <span className="event-card-date">{fmt(ev.date)}</span>
                </div>
                <div className="event-card-name">{ev.name}</div>
                <div className="event-card-circuit">{ev.circuitName}</div>
                <div className="event-card-attendees">
                  <span>👥</span>
                  <span>{ev.attendees?.length || 0} attending</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {user && (
        <Fab color="primary" aria-label="create event" sx={{ position: 'fixed', bottom: 24, right: 24 }}
          onClick={() => { setEditEvt(null); setOpenForm(true); }}>
          <Add />
        </Fab>
      )}

      <EventFormDialog
        open={openForm}
        onClose={() => { setOpenForm(false); setEditEvt(null); }}
        circuits={circuits}
        userId={user?.uid}
        userName={user?.displayName || 'User'}
        editEvent={editEvt}
        onSaved={loadData}
      />

      {detailEvt && (
        <EventDetailDialog
          open={Boolean(detailEvt)}
          onClose={() => setDetailEvt(null)}
          event={detailEvt}
          userId={user?.uid}
          isOwner={user?.uid === detailEvt.createdBy}
          onEdit={() => { setEditEvt(detailEvt); setDetailEvt(null); setOpenForm(true); }}
          onDelete={() => setConfirmDel(true)}
          onJoinChange={loadData}
        />
      )}

      <Dialog open={confirmDel} onClose={() => setConfirmDel(false)}
        PaperProps={{ sx: { borderRadius: 'var(--radius-xl)' } }}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>This will permanently remove the event for everyone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Calendar;
